import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import useService from '@/lib/hooks/useService';
import PreviewForAddGroupOrder from '../PreviewForGroupOrder';
import UploadImage from '../UploadImage';
import styles from './index.module.scss';
import type {
  AddOrderFormDataType,
  OrderForTableReceive,
  OrderFormInitialValues,
} from '../types';
import type { Uploads } from '@/types/database/uploads';

interface AddOrEditOrderProps {
  orderRecord?: OrderForTableReceive;
}

const { TextArea } = Input;
const inputNumberStyle = {
  width: 190,
};

const AddOrEditOrder = ({ orderRecord }: AddOrEditOrderProps) => {
  const [form] = Form.useForm<AddOrderFormDataType>();

  const [uploads, updateUploads, uploadsService] = useService<Uploads>({
    collectionName: 'uploads',
  });
  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenPreviewForGroupOrderModal, setIsOpenPreviewForGroupOrderModal] =
    useState(false);
  const [imageList, setImageList] = useState<Uploads[]>([]);

  const generateImageListForDisplayReceivedImage = useCallback(
    (imgUrl: string | undefined): Uploads[] => {
      if (imgUrl) {
        const foundUpload = uploads.find((upload) => {
          return upload.showPath === imgUrl;
        });
        return foundUpload !== undefined ? [foundUpload] : [];
      } else {
        return form.getFieldValue('uploadedImage');
      }
    },
    [form, uploads]
  );

  useEffect(() => {
    if (orderRecord !== undefined) {
      const imageList = generateImageListForDisplayReceivedImage(
        orderRecord.imgUrl
      );
      setImageList(imageList);
    }
  }, [generateImageListForDisplayReceivedImage, orderRecord]);

  const costsValidator = ({ getFieldValue }) => ({
    validator() {
      if (
        (getFieldValue('creditCardCost') !== '' &&
          getFieldValue('creditCardCost') !== null) ||
        (getFieldValue('pointsCost') !== '' &&
          getFieldValue('pointsCost') !== null)
      ) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('請輸入任一種類費用金額'));
    },
  });

  const deleteUploadedImage = async (id: string) => {
    await uploadsService.remove(id);
  };

  const closeOnlyAddModalOpened = () => {
    if (imageList.length !== 0) {
      deleteUploadedImage(imageList[0]._id);
      setImageList([]);
    }
    form.resetFields();
    setIsOpenAddModal(false);
  };

  const closeOnlyEditModalOpened = () => {
    if (imageList && orderRecord?.imgUrl !== imageList[0]?.showPath) {
      form.resetFields(['uploadedImage']);
      setImageList(
        generateImageListForDisplayReceivedImage(orderRecord?.imgUrl)
      );
    }
    setIsOpenEditModal(false);
  };

  const closeModal = () => {
    if (isOpenPreviewForGroupOrderModal && isOpenAddModal) {
      setIsOpenPreviewForGroupOrderModal(false);
      setImageList([]);
      form.resetFields();
      setIsOpenAddModal(false);
    } else if (isOpenPreviewForGroupOrderModal && isOpenEditModal) {
      setIsOpenPreviewForGroupOrderModal(false);
      setImageList(
        generateImageListForDisplayReceivedImage(orderRecord?.imgUrl)
      );
      setIsOpenEditModal(false);
    } else if (isOpenPreviewForGroupOrderModal === false && isOpenAddModal) {
      closeOnlyAddModalOpened();
    } else {
      closeOnlyEditModalOpened();
    }
    updateUploads();
  };

  const closePreviewForGroupOrderModal = () => {
    setIsOpenPreviewForGroupOrderModal(false);
  };

  const updateImgUrl = async (newImageList: Uploads[]) => {
    form.setFieldValue('uploadedImage', newImageList);
    setImageList(newImageList);
  };

  const getOrder = (): AddOrderFormDataType => {
    const formValues = form.getFieldsValue();
    if (orderRecord) {
      formValues.id = orderRecord._id;
      formValues.status = orderRecord.status;
    }
    return {
      ...formValues,
    };
  };

  const getEditFormInitialValues = (): OrderFormInitialValues => {
    if (orderRecord) {
      return {
        ...orderRecord,
        uploadedImage: generateImageListForDisplayReceivedImage(
          orderRecord?.imgUrl
        ),
        establishTime: dayjs(orderRecord.establishTime),
        startTime: dayjs(orderRecord.startTime),
        endTime: dayjs(orderRecord.endTime),
        creditCardCost: orderRecord?.cost[0].amount,
        pointsCost: orderRecord?.cost[1].amount,
      };
    } else {
      return {
        itemName: '',
        expirationTime: '',
        allergen: '',
        storageMethod: '',
        manufacturerName: '',
        manufacturerPhone: '',
        manufacturerAddress: '',
        itemIntroduction: '',
        itemSpec: '',
        notice: '',
        uploadedImage: [],
        establishTime: null,
        startTime: null,
        endTime: null,
        creditCardCost: null,
        pointsCost: null,
        minimumAmount: null,
        maximumAmount: null,
      };
    }
  };

  return (
    <>
      <Button
        type={orderRecord ? 'default' : 'primary'}
        onClick={() =>
          orderRecord ? setIsOpenEditModal(true) : setIsOpenAddModal(true)
        }
      >
        {orderRecord ? '編輯' : '建立團購'}
      </Button>
      <Modal
        open={isOpenEditModal || isOpenAddModal}
        footer={null}
        onCancel={closeModal}
        className={styles.modal}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          onFinish={() => setIsOpenPreviewForGroupOrderModal(true)}
          initialValues={getEditFormInitialValues()}
        >
          <div className={styles.formLeft}>
            <Form.Item
              label="團購名稱"
              name="itemName"
              rules={[{ required: true, message: '請輸入團購名稱' }]}
            >
              <Input placeholder="請輸入團購名稱" />
            </Form.Item>
            <Form.Item label="賞味期限" name="expirationTime">
              <Input placeholder="請輸入賞味期限 ex: 2023-06-15" />
            </Form.Item>
            <Form.Item label="過敏原" name="allergen">
              <Input placeholder="請輸入過敏原" />
            </Form.Item>
            <Form.Item label="保存方法" name="storageMethod">
              <Input placeholder="請輸入保存方法" />
            </Form.Item>
            <Form.Item label="廠商名稱" name="manufacturerName">
              <Input placeholder="請輸入廠商名稱" />
            </Form.Item>
            <Form.Item label="廠商電話" name="manufacturerPhone">
              <Input placeholder="請輸入廠商電話" />
            </Form.Item>
            <Form.Item label="廠商地址" name="manufacturerAddress">
              <Input placeholder="請輸入廠商地址" />
            </Form.Item>
            <Form.Item label="商品介紹" name="itemIntroduction">
              <TextArea
                placeholder="請輸入商品介紹"
                maxLength={100}
                rows={4}
                showCount
              />
            </Form.Item>
            <Form.Item label="商品包含" name="itemSpec">
              <TextArea
                placeholder="請輸入商品包含"
                maxLength={100}
                rows={4}
                showCount
              />
            </Form.Item>
            <Form.Item label="注意事項" name="notice">
              <TextArea
                placeholder="請輸入注意事項"
                maxLength={100}
                rows={4}
                showCount
              />
            </Form.Item>
          </div>
          <div className={styles.formRight}>
            <Form.Item
              label="上傳圖片"
              name="uploadedImage"
              valuePropName="fileList"
            >
              <UploadImage updateImgUrl={updateImgUrl} imageList={imageList} />
            </Form.Item>
            <Form.Item
              label="團購上架時間"
              name="establishTime"
              rules={[{ required: true, message: '請選擇團購上架時間' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                inputReadOnly
              />
            </Form.Item>
            <Form.Item
              label="團購起始時間"
              name="startTime"
              rules={[{ required: true, message: '請選擇團購起始時間' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                inputReadOnly
              />
            </Form.Item>
            <Form.Item
              label="團購截止時間"
              name="endTime"
              rules={[{ required: true, message: '請選擇團購截止時間' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                inputReadOnly
              />
            </Form.Item>
            <Form.Item label={<div className={styles.costTitle}>費用</div>}>
              <Form.Item
                label="信用卡金額"
                name="creditCardCost"
                rules={[costsValidator]}
              >
                <InputNumber
                  placeholder="請輸入信用卡費用金額"
                  min={0}
                  style={inputNumberStyle}
                />
              </Form.Item>
              <Form.Item
                label="點數金額"
                name="pointsCost"
                rules={[costsValidator]}
              >
                <InputNumber
                  placeholder="請輸入點數費用金額"
                  min={0}
                  style={inputNumberStyle}
                />
              </Form.Item>
            </Form.Item>
            <Form.Item
              label="成團最低訂單數量"
              name="minimumAmount"
              rules={[{ required: true, message: '請輸入成團最低訂單數量' }]}
            >
              <InputNumber
                placeholder="請輸入成團最低訂單數量"
                min={0}
                style={inputNumberStyle}
              />
            </Form.Item>
            <Form.Item
              label="團購訂單數量上限"
              name="maximumAmount"
              rules={[{ required: true, message: '請輸入團購訂單數量上限' }]}
            >
              <InputNumber
                placeholder="請輸入團購訂單數量上限"
                min={0}
                style={inputNumberStyle}
              />
            </Form.Item>
            <Form.Item className={styles.footerButtonsContainer}>
              <Space>
                <Button type="primary" onClick={closeModal}>
                  取消
                </Button>
                <Button htmlType="submit">預覽</Button>
              </Space>
            </Form.Item>
          </div>
        </Form>

        <PreviewForAddGroupOrder
          open={isOpenPreviewForGroupOrderModal}
          order={getOrder()}
          closePreviewForGroupOrderModal={closePreviewForGroupOrderModal}
          closeAllModalAfterAddOrEditGroupBuying={closeModal}
        />
      </Modal>
    </>
  );
};

export default AddOrEditOrder;
